/**
 * ADS-65 — session pageview count for GAM targeting (`pageview=3`).
 *
 * Atlas incremented only on engine bootstrap (not virtual PVs), persisted via
 * CacheManager with an inactivity TTL, and re-upped that TTL every bootstrap.
 * Concert stores `{ v, exp }` in localStorage and publishes via `addVariable`.
 */

export const PAGEVIEW_KEY = 'pageview';
export const PAGEVIEW_STORAGE_KEY = 'concert_pageview_counter';
export const DEFAULT_PAGEVIEW_INACTIVITY_MINUTES = 30;

/**
 * @param {unknown} minutes
 * @returns {number}
 */
export function pageviewInactivityMs(minutes) {
  const n = Number(minutes);
  const resolved = Number.isFinite(n) && n > 0 ? n : DEFAULT_PAGEVIEW_INACTIVITY_MINUTES;
  return resolved * 60 * 1000;
}

/**
 * Atlas CacheManager only persisted when consents allowed. Concert equivalent:
 * OneTrust analytics group C0002 / `analytics_storage === 'granted'`.
 *
 * @param {{ analytics_storage?: string }} [oneTrustConsentData]
 * @returns {boolean}
 */
export function canPersistPageview(oneTrustConsentData) {
  return oneTrustConsentData?.analytics_storage === 'granted';
}

function defaultStorage() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch (e) {
    return null;
  }
}

/**
 * @param {Storage} store
 * @param {number} now
 * @returns {number|null}
 */
function readStoredCount(store, now) {
  try {
    const raw = store.getItem(PAGEVIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const value = Number(parsed?.v);
    const exp = Number(parsed?.exp);
    if (!Number.isFinite(value) || !Number.isFinite(exp) || exp <= now) return null;
    return value;
  } catch (e) {
    return null;
  }
}

/**
 * @param {Storage} store
 * @param {number} count
 * @param {number} exp
 */
function writeStored(store, count, exp) {
  try {
    store.setItem(PAGEVIEW_STORAGE_KEY, JSON.stringify({ v: count, exp }));
  } catch (e) {
    // quota / private mode
  }
}

/**
 * Get current count (0 if missing/expired), increment, persist if allowed, return string.
 * Atlas: `(Number(stored) || 0) + 1` then set expiry to now + inactivity.
 *
 * @param {{ canPersist?: boolean, now?: number, inactivityMs?: number, storage?: Storage|null }} [opts]
 * @returns {string}
 */
export function getAndIncrementPageview(opts = {}) {
  const now = opts.now ?? Date.now();
  const ttl = opts.inactivityMs ?? pageviewInactivityMs();
  const store = opts.storage === undefined ? defaultStorage() : opts.storage;

  if (!opts.canPersist || !store) {
    return '1';
  }

  const stored = readStoredCount(store, now);
  const count = (stored || 0) + 1;
  writeStored(store, count, now + ttl);
  return String(count);
}
